import { useEffect } from "react";

import { sanitizeInvisibleCharacters } from "utilities";

export const useCleanPaste = (): void => {
  useEffect(() => {
    // Check if we already have a paste listener to prevent duplicates during hot reload
    if ((window as any).__apolloPasteListenerAdded) {
      return;
    }

    const handlePaste = (event: ClipboardEvent): void => {
      const target = event.target as HTMLElement;

      // Only process if the target is an input or textarea element
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) {
        // Get the pasted text from clipboard
        const clipboardData = event.clipboardData;
        if (clipboardData) {
          const pastedText = clipboardData.getData("text/plain");
          if (pastedText) {
            const sanitizedText = sanitizeInvisibleCharacters(pastedText);

            // If the text was modified, prevent default and insert sanitized text
            if (sanitizedText !== pastedText) {
              event.preventDefault();
              event.stopPropagation();

              // Insert the sanitized text at the cursor position
              const input = target as HTMLInputElement | HTMLTextAreaElement;
              const start = input.selectionStart || 0;
              const end = input.selectionEnd || 0;
              const value = input.value;
              const newValue = value.substring(0, start) + sanitizedText + value.substring(end);

              // Set the value using the native setter for the specific element type
              const prototype =
                target.tagName === "INPUT"
                  ? HTMLInputElement.prototype
                  : HTMLTextAreaElement.prototype;

              const nativeValueSetter = Object.getOwnPropertyDescriptor(prototype, "value")?.set;

              if (nativeValueSetter) {
                // Use the native setter to properly update the value
                nativeValueSetter.call(input, newValue);
              } else {
                input.value = newValue;
              }

              input.setSelectionRange(start + sanitizedText.length, start + sanitizedText.length);

              // Trigger multiple events to ensure React components are properly notified
              const inputEvent = new Event("input", { bubbles: true, cancelable: true });
              const changeEvent = new Event("change", { bubbles: true, cancelable: true });

              input.dispatchEvent(inputEvent);
              input.dispatchEvent(changeEvent);
            }
          }
        }
      }
    };

    document.addEventListener("paste", handlePaste);

    // Mark window as having our paste listener
    (window as any).__apolloPasteListenerAdded = true;

    return () => {
      document.removeEventListener("paste", handlePaste);
      (window as any).__apolloPasteListenerAdded = false;
    };
  }, []);
};
